import {
  fetchUserByEmail,
  fetchUsers,
  updateUser,
  updateAdminNavPreferences as updateAdminNavPreferencesAPI,
  disableUserMfa as disableUserMfaAPI
} from '../api/users';
import { applyTagToUsers as applyTagToUsersAPI, deleteUserTag as deleteUserTagAPI } from '../api/tags';
import { getNavPreferencesFromUiFlags, serializeNavKeys } from '../helpers/nav_helpers';
import { cognitoSignOut } from '../Auth';

export const GET_USERS_REQUEST = 'GET_USERS_REQUEST';
export const GET_USERS_SUCCESS = 'GET_USERS_SUCCESS';
export const CLEAR_USERS = 'CLEAR_USERS';

export const APPLY_TAG_TO_USERS_REQUEST = 'APPLY_TAG_TO_USERS_REQUEST';
export const APPLY_TAG_TO_USERS_SUCCESS = 'APPLY_TAG_TO_USERS_SUCCESS';

export const DELETE_USER_TAG_REQUEST = 'DELETE_USER_TAG_REQUEST';
export const DELETE_USER_TAG_SUCCESS = 'DELETE_USER_TAG_SUCCESS';

export const SET_CURRENT_USER = 'SET_CURRENT_USER';
export const LOGOUT_USER_SUCCESS = 'LOGOUT_USER_SUCCESS';

export const UPDATE_USER_IN_STATE = 'UPDATE_USER_IN_STATE';
export const UPDATE_USER_FIELD_REQUEST = 'UPDATE_USER_FIELD_REQUEST';
export const UPDATE_USER_FIELD_SUCCESS = 'UPDATE_USER_FIELD_SUCCESS';
export const UPDATE_USER_FIELD_ERROR = 'UPDATE_USER_FIELD_ERROR';

export const updateUserInState = (userId, updates) => dispatch => {
  dispatch({
    type: UPDATE_USER_IN_STATE,
    userId,
    updates
  });
};

// Clears a user's MFA (server-gated to super admins). Drives the row spinner via `_updating`, then
// nulls `mfaEnabledAt` on success so the "Disable MFA" cell reverts to its inactive state.
export const disableUserMfa = userId => async dispatch => {
  dispatch(updateUserInState(userId, { _updating: 'mfaEnabledAt' }));
  try {
    await disableUserMfaAPI(userId);
    dispatch(updateUserInState(userId, { mfaEnabledAt: null, _updating: null }));
  } catch (error) {
    dispatch(updateUserInState(userId, { _updating: null }));
    throw error;
  }
};

export const updateUserField = ({ userId, fieldName, newValue, oldValue, additionalData }) => async dispatch => {
  // Optimistic update request
  dispatch({
    type: UPDATE_USER_FIELD_REQUEST,
    userId,
    fieldName,
    newValue
  });

  try {
    await updateUser({ id: userId }, { [fieldName]: newValue, ...additionalData });

    dispatch({
      type: UPDATE_USER_FIELD_SUCCESS,
      userId,
      fieldName
    });
  } catch (error) {
    // Revert on error
    dispatch({
      type: UPDATE_USER_FIELD_ERROR,
      userId,
      fieldName,
      oldValue,
      error
    });

    throw error;
  }
};

// Search ID to prevent race conditions
let currentSearchId = 0;

export const getUsers = (params = {}) => async (dispatch, getState) => {
  // Generate unique ID for this search
  const searchId = ++currentSearchId;

  dispatch({
    type: GET_USERS_REQUEST,
    searchId,
    params
  });

  const response = await fetchUsers(params);

  // Only dispatch if this is still the latest search
  if (searchId === currentSearchId) {
    return dispatch({
      type: GET_USERS_SUCCESS,
      users: response.users,
      pagination: response.pagination,
      aggregates: response.aggregates,
      searchId
    });
  }
};

export const clearUsers = () => dispatch => {
  return dispatch({
    type: CLEAR_USERS
  });
};

export const applyTagToUsers = (tag, users) => async (dispatch, getState) => {
  dispatch({
    type: 'APPLY_TAG_TO_USERS_REQUEST',
    tag,
    users
  });
  await applyTagToUsersAPI(tag, users);
  return dispatch({
    type: 'APPLY_TAG_TO_USERS_SUCCESS',
    tag,
    users
  });
};

export const deleteUserTag = (tag, user) => async (dispatch, getState) => {
  dispatch({
    type: 'DELETE_USER_TAG_REQUEST',
    tag,
    user
  });
  await deleteUserTagAPI(tag, user);
  return dispatch({
    type: 'DELETE_USER_TAG_SUCCESS'
  });
};

export const syncCurrentUser = email => async dispatch => {
  const { user } = await fetchUserByEmail({ email });

  const scopes = user?.admin_scopes?.scopes?.split(',').filter(Boolean) ?? [];
  if (!user?.isAdmin || scopes.length === 0) {
    cognitoSignOut();
    window.location = '/';
    return null;
  }

  const currentUser = {
    id: user.id,
    username: user.username,
    name: user.name,
    email: user.email,
    isAdmin: user.isAdmin,
    scopes,
    navPreferences: getNavPreferencesFromUiFlags(user?.settings?.uiFlags)
  };

  dispatch({ type: SET_CURRENT_USER, currentUser });
  return currentUser;
};

// Persist only — no dispatch to the currentUser slice, which App subscribes to and would remount the page.
export const updateAdminNavPreferences = updates => async (dispatch, getState) => {
  const userId = getState().users.currentUser?.id;
  if (!userId) return;

  const flags = {};
  if (updates.pinned !== undefined) flags.adminNavPinned = serializeNavKeys(updates.pinned);
  if (updates.recent !== undefined) flags.adminNavRecent = serializeNavKeys(updates.recent);

  try {
    await updateAdminNavPreferencesAPI(userId, flags);
  } catch {
    window.ALERT.error('Could not save your menu layout — it may reset on reload.');
  }
};

export const logoutUser = () => async (dispatch, getState) => {
  await cognitoSignOut();
  return dispatch({ type: LOGOUT_USER_SUCCESS });
};
